import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import logoSrc from "../assets/logo.png";
import { MarketingNav } from "@/components/layout/MarketingNav";
import {
  ArrowRight,
  BookOpen,
  Brain,
  CheckCircle2,
  ChevronRight,
  FileText,
  GitBranch,
  Link2,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
  Lock,
  Globe,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const C = {
  navy: "#07081a",
  navyMid: "#0d0f2d",
  navyCard: "#11154a",
  purple: "#6C63FF",
  purpleDim: "#4B44CC",
  purpleGlow: "rgba(108,99,255,0.18)",
  lime: "#a8e063",
  limeGlow: "rgba(168,224,99,0.15)",
  white: "#ffffff",
  slate: "#c4c9e8",
  dimSlate: "#7880a8",
  border: "rgba(108,99,255,0.2)",
  cardBg: "rgba(17,21,74,0.6)",
  glassBg: "rgba(11,13,40,0.7)",
};

// ─── SNAPSHOTS ────────────────────────────────────────────────────────────────

const PolicyLibrarySnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <BookOpen size={14} style={{ color: C.purple }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Policy Library</span>
      <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(108,99,255,0.15)", color: C.purple }}>31 Policies</span>
    </div>
    <div className="p-3 space-y-2">
      {[
        { name: "Data Privacy Policy", status: "Approved", cat: "Compliance", color: C.lime },
        { name: "Remote Work Policy", status: "Under Review", cat: "HR", color: "#f59e0b" },
        { name: "Acceptable Use Policy", status: "Approved", cat: "IT Security", color: C.lime },
        { name: "Leave & Absence Policy", status: "Draft", cat: "HR", color: C.dimSlate },
        { name: "Information Security", status: "Pending Approval", cat: "IT", color: "#a78bfa" },
      ].map((p, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: i === 0 ? "rgba(108,99,255,0.1)" : "transparent" }}>
          <FileText size={13} style={{ color: C.purple, flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate" style={{ color: C.white }}>{p.name}</div>
            <div className="text-[10px]" style={{ color: C.dimSlate }}>{p.cat}</div>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${p.color}18`, color: p.color, border: `1px solid ${p.color}30` }}>{p.status}</span>
        </div>
      ))}
    </div>
    <div className="px-3 pb-3 flex gap-2">
      <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}` }}>
        <Search size={11} style={{ color: C.dimSlate }} />
        <span className="text-[10px]" style={{ color: C.dimSlate }}>Search policies…</span>
      </div>
    </div>
  </div>
);

const AICreatorSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <Brain size={14} style={{ color: C.lime }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>AI Policy Creator</span>
      <span className="ml-2 flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${C.lime}18`, color: C.lime }}><Sparkles size={9} /> AI-Powered</span>
    </div>
    <div className="p-4 space-y-3">
      <div className="rounded-lg p-3" style={{ background: "rgba(108,99,255,0.08)", border: `1px solid ${C.border}` }}>
        <div className="text-[10px] mb-1" style={{ color: C.dimSlate }}>You</div>
        <div className="text-xs" style={{ color: C.slate }}>Create a remote work policy for a tech company with 200 employees, including security requirements and productivity guidelines.</div>
      </div>
      <div className="rounded-lg p-3" style={{ background: `${C.lime}08`, border: `1px solid ${C.lime}20` }}>
        <div className="flex items-center gap-1 text-[10px] mb-2" style={{ color: C.lime }}><Brain size={9} /><span>ManagEaze AI</span></div>
        <div className="text-xs space-y-1" style={{ color: C.slate }}>
          <div className="font-semibold" style={{ color: C.white }}>Remote Work Policy v1.0</div>
          <div className="text-[10px]">1. Purpose &amp; Scope · 2. Eligibility Criteria · 3. Security Requirements · 4. Data Protection…</div>
        </div>
        <div className="mt-2 flex gap-1">
          {["Draft →", "Review", "Export"].map(a => (
            <button key={a} className="text-[9px] px-2 py-0.5 rounded" style={{ background: "rgba(108,99,255,0.2)", color: C.purple }}>{a}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 text-[10px]" style={{ color: C.dimSlate }}>
        <Zap size={9} style={{ color: C.lime }} />
        Generated in 3.2s · Grounded in your company context
      </div>
    </div>
  </div>
);

const WorkflowSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <GitBranch size={14} style={{ color: C.purple }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Approval Workflow</span>
    </div>
    <div className="p-3 space-y-2">
      {[
        { stage: "Draft Created", by: "Saad H.", time: "2h ago", done: true },
        { stage: "Sent for Review", by: "Meeran F.", time: "1h ago", done: true },
        { stage: "Pending Approval", by: "Faisal C.", time: "30m ago", done: false, current: true },
        { stage: "Final Approval", by: "—", time: "", done: false },
      ].map((s, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="flex flex-col items-center" style={{ minWidth: 24 }}>
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: s.done ? C.lime : s.current ? C.purple : "transparent", border: `2px solid ${s.done ? C.lime : s.current ? C.purple : C.dimSlate}` }}>
              {s.done && <CheckCircle2 size={11} color={C.navy} strokeWidth={3} />}
              {s.current && <div className="w-1.5 h-1.5 rounded-full" style={{ background: C.white }} />}
            </div>
            {i < 3 && <div className="w-px h-4 mt-0.5" style={{ background: s.done ? `${C.lime}50` : `${C.dimSlate}30` }} />}
          </div>
          <div className="pb-2">
            <div className="text-xs font-medium" style={{ color: s.current ? C.white : s.done ? C.slate : C.dimSlate }}>{s.stage}</div>
            {s.by !== "—" && <div className="text-[10px]" style={{ color: C.dimSlate }}>{s.by} · {s.time}</div>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ComplianceSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <ShieldCheck size={14} style={{ color: C.lime }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Compliance Dashboard</span>
    </div>
    <div className="p-3 grid grid-cols-2 gap-2 mb-2">
      {[
        { label: "Health Score", value: "86%", color: C.lime },
        { label: "Tasks Done", value: "18/21", color: C.purple },
        { label: "Auto-Generated", value: "21", color: "#a78bfa" },
        { label: "Overdue", value: "1", color: "#f87171" },
      ].map((s, i) => (
        <div key={i} className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
          <div className="text-[9px] mb-1" style={{ color: C.dimSlate }}>{s.label}</div>
          <div className="text-base font-bold" style={{ color: s.color }}>{s.value}</div>
        </div>
      ))}
    </div>
    <div className="px-3 pb-3 space-y-1.5">
      <div className="text-[10px] font-semibold mb-1" style={{ color: C.dimSlate }}>Auto-generated from policies</div>
      {[
        { t: "Conduct Regular Safety Audits", done: false },
        { t: "Evaluate Training Effectiveness", done: true },
        { t: "Assess Training Needs Annually", done: false },
      ].map((task, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: task.done ? C.lime : "transparent", border: `2px solid ${task.done ? C.lime : C.dimSlate}` }} />
          <span className="text-[10px]" style={{ color: task.done ? C.dimSlate : C.slate, textDecoration: task.done ? "line-through" : "none" }}>{task.t}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── PAGE ─────────────────────────────────────────────────────────────────────

export default function PolicyMarketing() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.from(".pm-hero-badge", { y: -20, opacity: 0, duration: 0.5, delay: 0.1, ease: "back.out(1.5)" });
      gsap.from(".pm-hero-line", { y: 60, opacity: 0, duration: 0.7, stagger: 0.15, delay: 0.3, ease: "power3.out" });
      gsap.from(".pm-hero-sub", { y: 20, opacity: 0, duration: 0.5, delay: 0.7, ease: "power2.out" });
      gsap.from(".pm-hero-cta", { y: 20, opacity: 0, duration: 0.5, delay: 0.9, ease: "power2.out" });
      gsap.from(".pm-hero-stat", { y: 30, opacity: 0, duration: 0.5, stagger: 0.1, delay: 1.0, ease: "back.out(1.2)" });

      // Floating orbs
      gsap.utils.toArray<Element>(".pm-orb").forEach((orb, i) => {
        gsap.to(orb, { y: i % 2 === 0 ? -18 : 18, duration: 2.5 + i * 0.4, yoyo: true, repeat: -1, ease: "sine.inOut", delay: i * 0.3 });
      });

      // Scroll-triggered sections
      gsap.utils.toArray<Element>(".pm-fade").forEach((el) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
          y: 50, opacity: 0, duration: 0.8, ease: "power2.out",
        });
      });

      gsap.utils.toArray<Element>(".pm-stagger").forEach((container) => {
        gsap.from((container as HTMLElement).querySelectorAll(".pm-child"), {
          scrollTrigger: { trigger: container, start: "top 90%", toggleActions: "play none none none" },
          y: 40, opacity: 0, duration: 0.6, stagger: 0.12, ease: "power2.out",
        });
      });

      // Stats counter
      gsap.utils.toArray<Element>(".pm-stat-num").forEach((el) => {
        ScrollTrigger.create({
          trigger: el,
          start: "top 90%",
          once: true,
          onEnter: () => {
            const raw = (el as HTMLElement).dataset.value || "";
            const isPercent = raw.includes("%");
            const isX = raw.includes("×");
            const num = parseFloat(raw);
            if (!isNaN(num)) {
              const obj = { val: 0 };
              gsap.to(obj, {
                val: num, duration: 1.8, ease: "power2.out",
                onUpdate: () => { (el as HTMLElement).textContent = Math.round(obj.val) + (isPercent ? "%" : isX ? "×" : ""); },
                onComplete: () => { (el as HTMLElement).textContent = raw; },
              });
            }
          },
        });
      });

      // CTA glow pulse
      gsap.to(".pm-cta-glow", { boxShadow: "0 0 60px 10px rgba(108,99,255,0.4)", duration: 1.5, yoyo: true, repeat: -1, ease: "sine.inOut" });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  const stats = [
    { num: "75%", label: "Faster policy cycle time" },
    { num: "98%", label: "Policy acknowledgment rate" },
    { num: "4×", label: "Compliance tasks closed faster" },
    { num: "100%", label: "Audit-ready, always" },
  ];

  const capabilities = [
    { icon: <Brain size={22} />, color: C.lime, title: "AI-Powered Creation", desc: "Draft compliant policies in minutes using your company context, not generic templates." },
    { icon: <GitBranch size={22} />, color: C.purple, title: "Approval Workflows", desc: "Multi-stage review and sign-off with role-based routing and a full audit trail." },
    { icon: <RefreshCw size={22} />, color: "#a78bfa", title: "Version Control & Renewals", desc: "Track every change, manage expiry dates, and trigger renewal cycles automatically." },
    { icon: <Link2 size={22} />, color: "#34d399", title: "Compliance Linking", desc: "Approved policies auto-generate compliance tasks and push obligations to the right teams." },
  ];

  const spotlights = [
    {
      heading: "From blank page to board-ready in minutes.",
      body: "AI drafts policies grounded in your company's actual context — industry, size, risk profile. No generic boilerplate.",
      bullets: ["Auto-structured sections based on policy type", "Regulatory alignment suggestions", "Tone & readability scoring", "Instant export to review workflow"],
      color: C.lime,
      icon: <Brain size={20} />,
      snapshot: <AICreatorSnapshot />,
      reverse: false,
    },
    {
      heading: "No more chasing approvals in email.",
      body: "Route policies through exactly the right reviewers, in the right order, with automatic escalations and deadline tracking.",
      bullets: ["Multi-stage routing with role-based assignments", "One-click approvals with inline comments", "Deadline reminders and escalation rules", "Full immutable audit trail"],
      color: C.purple,
      icon: <GitBranch size={20} />,
      snapshot: <WorkflowSnapshot />,
      reverse: true,
    },
    {
      heading: "Approved policies that actually drive action.",
      body: "When a policy is approved, ManagEaze automatically generates compliance tasks, assigns owners, and tracks completion — closing the gap between policy and practice.",
      bullets: ["Auto-task generation from policy obligations", "Department and owner assignment", "Evidence collection and health score impact", "Overdue alerts and priority flags"],
      color: "#34d399",
      icon: <ShieldCheck size={20} />,
      snapshot: <ComplianceSnapshot />,
      reverse: false,
    },
  ];

  const approvalFlow = [
    { role: "Creator", icon: <FileText size={20} />, color: C.purple, desc: "Draft policies with AI assistance, grounded in your company context and regulatory requirements." },
    { role: "Reviewer", icon: <Search size={20} />, color: "#a78bfa", desc: "Inline comments, suggested edits, and one-click status updates — review in the platform, not in email." },
    { role: "Approver", icon: <CheckCircle2 size={20} />, color: C.lime, desc: "Full policy context, version history, and compliance implications before you sign off." },
    { role: "Auditor", icon: <ShieldCheck size={20} />, color: "#34d399", desc: "Immutable audit trail, evidence packages, and policy version history — everything you need, always ready." },
  ];

  return (
    <div
      ref={containerRef}
      style={{ background: C.navy, color: C.white, fontFamily: "'DM Sans', ui-sans-serif, sans-serif", minHeight: "100vh", overflowX: "hidden" }}
    >
      <MarketingNav activePage="policy" />

      {/* ─── HERO ─── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-28 pb-20 overflow-hidden">
        {/* Background orbs */}
        <div className="pm-orb absolute rounded-full pointer-events-none" style={{ width: 500, height: 500, background: "radial-gradient(circle, rgba(108,99,255,0.15) 0%, transparent 70%)", top: "5%", left: "5%", filter: "blur(60px)" }} />
        <div className="pm-orb absolute rounded-full pointer-events-none" style={{ width: 400, height: 400, background: "radial-gradient(circle, rgba(168,224,99,0.1) 0%, transparent 70%)", bottom: "10%", right: "8%", filter: "blur(80px)" }} />
        <div className="pm-orb absolute rounded-full pointer-events-none" style={{ width: 300, height: 300, background: "radial-gradient(circle, rgba(108,99,255,0.08) 0%, transparent 70%)", top: "50%", right: "20%", filter: "blur(50px)" }} />

        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(108,99,255,0.15) 1px, transparent 1px)", backgroundSize: "30px 30px", maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black, transparent)" }} />

        <div className="pm-hero-badge inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 text-xs font-semibold" style={{ background: "rgba(108,99,255,0.12)", border: `1px solid ${C.border}`, color: C.purple }}>
          <BookOpen size={12} />
          <span>Policy Management</span>
        </div>

        <h1 className="relative max-w-4xl mx-auto mb-6" style={{ lineHeight: 1.1 }}>
          <div className="pm-hero-line text-5xl md:text-7xl font-black tracking-tighter block" style={{ color: C.white }}>Policy Management</div>
          <div className="pm-hero-line text-5xl md:text-7xl font-black tracking-tighter block" style={{ background: "linear-gradient(90deg, #6C63FF, #a8e063)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>That Actually Gets Done.</div>
        </h1>

        <p className="pm-hero-sub max-w-xl mx-auto text-xl mb-10" style={{ color: C.dimSlate, lineHeight: 1.6 }}>
          One platform to <span style={{ color: C.slate }}>create</span>, <span style={{ color: C.slate }}>approve</span>, <span style={{ color: C.slate }}>distribute</span>, and <span style={{ color: C.slate }}>track</span> every policy — powered by AI, built for regulated organizations.
        </p>

        <div className="pm-hero-cta flex flex-col sm:flex-row gap-4 items-center mb-20">
          <button className="pm-cta-glow flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:scale-105" style={{ background: C.purple, color: C.white }}>
            Start Free Trial <ArrowRight size={16} />
          </button>
          <button className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:opacity-80" style={{ border: `1px solid ${C.border}`, color: C.slate }}>
            Book a Demo <ChevronRight size={16} />
          </button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl w-full">
          {stats.map((s, i) => (
            <div
              key={i}
              className="pm-hero-stat rounded-xl px-4 py-4 text-center"
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, backdropFilter: "blur(8px)" }}
            >
              <div className="pm-stat-num text-4xl font-black mb-1" data-value={s.num} style={{ background: "linear-gradient(135deg, #6C63FF, #a8e063)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {s.num}
              </div>
              <div className="text-sm" style={{ color: C.dimSlate }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Hero visual */}
        <div className="mt-16 w-full max-w-lg">
          <PolicyLibrarySnapshot />
        </div>
      </section>

      {/* ─── CAPABILITIES GRID ─── */}
      <section className="py-24 px-6 max-w-6xl mx-auto pm-fade">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: `${C.purple}15`, border: `1px solid ${C.border}`, color: C.purple }}>
            <Zap size={11} /> Core Capabilities
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4" style={{ color: C.white }}>Everything your policy program needs.</h2>
          <p className="max-w-lg mx-auto text-lg" style={{ color: C.dimSlate }}>From first draft to full compliance — ManagEaze handles the entire lifecycle so nothing slips.</p>
        </div>

        <div className="pm-stagger grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {capabilities.map((cap, i) => (
            <motion.div
              key={i}
              className="pm-child rounded-2xl p-6 group cursor-pointer"
              whileHover={{ y: -4 }}
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, backdropFilter: "blur(8px)" }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${cap.color}15`, color: cap.color, border: `1px solid ${cap.color}30` }}>
                {cap.icon}
              </div>
              <h3 className="font-bold text-base mb-2" style={{ color: C.white }}>{cap.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{cap.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FEATURE SPOTLIGHTS ─── */}
      <section className="py-8 px-6 max-w-6xl mx-auto space-y-28">
        {spotlights.map((spot, i) => (
          <motion.div
            key={i}
            className={`grid md:grid-cols-2 gap-12 items-center pm-fade`}
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            {/* Copy */}
            <div className={spot.reverse ? "md:order-2" : ""}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: `${spot.color}15`, color: spot.color, border: `1px solid ${spot.color}30` }}>
                {spot.icon}
              </div>
              <h3 className="text-3xl md:text-4xl font-black tracking-tight mb-4" style={{ color: C.white }}>{spot.heading}</h3>
              <p className="text-base leading-relaxed mb-7" style={{ color: C.dimSlate }}>{spot.body}</p>
              <ul className="space-y-3">
                {spot.bullets.map((b, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm" style={{ color: C.slate }}>
                    <CheckCircle2 size={15} style={{ color: spot.color, flexShrink: 0 }} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Snapshot */}
            <div className={spot.reverse ? "md:order-1" : ""}>
              {spot.snapshot}
            </div>
          </motion.div>
        ))}
      </section>

      {/* ─── APPROVAL FLOW ─── */}
      <section className="py-28 px-6 max-w-5xl mx-auto pm-fade">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: `${C.lime}15`, border: `1px solid ${C.lime}30`, color: C.lime }}>
            <Users size={11} /> Built for Every Role
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4" style={{ color: C.white }}>One workflow.<br />Every stakeholder covered.</h2>
          <p className="max-w-lg mx-auto text-base" style={{ color: C.dimSlate }}>ManagEaze routes policies through every stage automatically — each role gets exactly what they need to play their part.</p>
        </div>

        {/* Flow cards */}
        <div className="pm-stagger relative">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-px" style={{ background: `linear-gradient(to right, ${C.purple}60, #a78bfa60, ${C.lime}60, #34d39960)` }} />

          <div className="grid md:grid-cols-4 gap-6">
            {approvalFlow.map((step, i) => (
              <div key={i} className="pm-child flex flex-col items-center text-center">
                {/* Step number + icon */}
                <div className="relative mb-5">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: `${step.color}15`, border: `1px solid ${step.color}30`, color: step.color }}>
                    {step.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black" style={{ background: step.color, color: C.navy }}>
                    {i + 1}
                  </div>
                </div>
                <div className="font-bold text-base mb-2" style={{ color: C.white }}>{step.role}</div>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{step.desc}</p>
                {/* Mobile connector */}
                {i < approvalFlow.length - 1 && (
                  <div className="md:hidden mt-4 flex items-center justify-center">
                    <ChevronRight size={16} style={{ color: C.dimSlate }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER CTA ─── */}
      <section className="py-20 px-6 max-w-6xl mx-auto pm-fade">
        <div className="rounded-3xl p-10 md:p-16 text-center" style={{ background: "linear-gradient(135deg, rgba(108,99,255,0.15), rgba(168,224,99,0.08))", border: `1px solid ${C.border}` }}>
          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-8 mb-10">
            {[
              { icon: <Lock size={18} />, label: "Role-Based Access Control" },
              { icon: <ShieldCheck size={18} />, label: "Audit Trail on Every Action" },
              { icon: <Globe size={18} />, label: "NVIDIA Inception Member" },
              { icon: <Users size={18} />, label: "Enterprise Multi-Tenancy" },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-medium" style={{ color: C.slate }}>
                <span style={{ color: C.purple }}>{t.icon}</span>
                {t.label}
              </div>
            ))}
          </div>

          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4" style={{ color: C.white }}>Ready to bring order to your policy program?</h2>
          <p className="max-w-lg mx-auto text-base mb-8" style={{ color: C.dimSlate }}>Join regulated organizations using ManagEaze to create, approve, and track policies — without the chaos.</p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button className="pm-cta-glow flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:scale-105" style={{ background: C.purple, color: C.white }}>
              Start Free Trial <ArrowRight size={16} />
            </button>
            <button className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:opacity-80" style={{ border: `1px solid ${C.border}`, color: C.slate }}>
              Book a Demo
            </button>
          </div>

          <p className="text-xs" style={{ color: C.dimSlate }}>No credit card required · SOC 2 compliant · Onboard in under a day</p>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-12 px-8" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img src={logoSrc} alt="ManagEaze" style={{ height: 28, width: "auto" }} />
          <p className="text-xs text-center" style={{ color: C.dimSlate }}>The all-in-one GRC platform · Policy, Compliance, Risk, Governance</p>
          <p className="text-xs" style={{ color: C.dimSlate }}>© 2026 ManagEaze · All rights reserved</p>
        </div>
      </footer>
    </div>
  );
}
